import React, { useState } from 'react';
import { formatNumber, calculateMortgage } from './propertyData';

function PropertyModal({ property, isFavorite, onToggleFavorite, onClose }) {
  const p = property;
  const [imgIndex, setImgIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('details');
  const [downPayment, setDownPayment] = useState(20);
  const [interestRate, setInterestRate] = useState(6.5);
  const [loanTerm, setLoanTerm] = useState(30);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: `Hi, I'm interested in ${p.title}. Please send me more details.` });
  const [formSent, setFormSent] = useState(false);

  const monthlyPayment = calculateMortgage(p.price, downPayment, interestRate, loanTerm);
  const totalPayment = monthlyPayment * loanTerm * 12;
  const totalInterest = totalPayment - p.price * (1 - downPayment / 100);

  return (
    <div className="re-modal-overlay" onClick={onClose}>
      <div className="re-modal" onClick={(e) => e.stopPropagation()}>
        <button className="re-modal-close" onClick={onClose}>&times;</button>

        {/* Image Gallery */}
        <div className="re-modal-gallery">
          <span className="re-modal-emoji">{p.images[imgIndex]}</span>
          {p.images.length > 1 && (
            <>
              <button className="re-img-nav prev" onClick={() => setImgIndex((i) => (i - 1 + p.images.length) % p.images.length)}>‹</button>
              <button className="re-img-nav next" onClick={() => setImgIndex((i) => (i + 1) % p.images.length)}>›</button>
            </>
          )}
          <div className="re-modal-gallery-badges">
            {p.isNew && <span className="re-badge new">New Listing</span>}
            {p.priceReduced && <span className="re-badge reduced">Price Reduced</span>}
            {p.featured && <span className="re-badge featured">Featured</span>}
          </div>
          <button className={`re-fav-btn large ${isFavorite ? 'active' : ''}`} onClick={onToggleFavorite}>
            ♥ {isFavorite ? 'Saved' : 'Save'}
          </button>
          <div className="re-modal-img-thumbs">
            {p.images.map((img, i) => (
              <button key={i} className={`re-thumb ${i === imgIndex ? 'active' : ''}`} onClick={() => setImgIndex(i)}>
                {img}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="re-modal-content">
          {/* Header */}
          <div className="re-modal-header">
            <div>
              <h2 className="re-modal-title">{p.title}</h2>
              <p className="re-modal-location">📍 {p.location}</p>
            </div>
            <div className="re-modal-price-col">
              <span className="re-modal-price">${formatNumber(p.price)}</span>
              {p.originalPrice && <span className="re-modal-original">${formatNumber(p.originalPrice)}</span>}
              <span className="re-modal-ppsqft">${Math.round(p.price / p.sqft)}/sqft</span>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="re-modal-quick-stats">
            <div className="re-quick-stat"><span className="re-qs-icon">🛏️</span><span className="re-qs-value">{p.beds}</span><span className="re-qs-label">Bedrooms</span></div>
            <div className="re-quick-stat"><span className="re-qs-icon">🚿</span><span className="re-qs-value">{p.baths}</span><span className="re-qs-label">Bathrooms</span></div>
            <div className="re-quick-stat"><span className="re-qs-icon">📐</span><span className="re-qs-value">{formatNumber(p.sqft)}</span><span className="re-qs-label">Sq Ft</span></div>
            <div className="re-quick-stat"><span className="re-qs-icon">🚗</span><span className="re-qs-value">{p.garage || 0}</span><span className="re-qs-label">Garage</span></div>
            <div className="re-quick-stat"><span className="re-qs-icon">📅</span><span className="re-qs-value">{p.yearBuilt}</span><span className="re-qs-label">Year Built</span></div>
            <div className="re-quick-stat"><span className="re-qs-icon">🏷️</span><span className="re-qs-value">{p.type}</span><span className="re-qs-label">Type</span></div>
          </div>

          {/* Tabs */}
          <div className="re-modal-tabs">
            {['details', 'mortgage', 'contact'].map((tab) => (
              <button key={tab} className={`re-modal-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {/* Details Tab */}
          {activeTab === 'details' && (
            <div className="re-tab-content">
              <div className="re-detail-section">
                <h3>Description</h3>
                <p className="re-modal-desc">{p.description}</p>
              </div>
              <div className="re-detail-section">
                <h3>Amenities</h3>
                <div className="re-amenities">
                  {p.amenities.map((a) => (
                    <span key={a} className="re-amenity">✓ {a}</span>
                  ))}
                </div>
              </div>
              <div className="re-detail-section">
                <h3>Property Details</h3>
                <div className="re-details-grid">
                  <div className="re-detail-item"><span>Property Type</span><strong>{p.type}</strong></div>
                  <div className="re-detail-item"><span>Year Built</span><strong>{p.yearBuilt}</strong></div>
                  <div className="re-detail-item"><span>Lot Size</span><strong>{p.lot ? `${formatNumber(parseInt(p.lot))} sqft` : 'N/A'}</strong></div>
                  <div className="re-detail-item"><span>HOA Fee</span><strong>{p.hoa ? `$${p.hoa}/mo` : 'None'}</strong></div>
                  <div className="re-detail-item"><span>Days on Market</span><strong>{p.daysOnMarket}</strong></div>
                  <div className="re-detail-item"><span>Views</span><strong>{formatNumber(p.views)}</strong></div>
                </div>
              </div>
            </div>
          )}

          {/* Mortgage Tab */}
          {activeTab === 'mortgage' && (
            <div className="re-tab-content">
              <div className="re-mortgage">
                <div className="re-mortgage-result">
                  <span className="re-mortgage-label">Estimated Monthly Payment</span>
                  <span className="re-mortgage-amount">${formatNumber(monthlyPayment)}/mo</span>
                </div>
                <div className="re-mortgage-controls">
                  <div className="re-mortgage-control">
                    <label>Down Payment: {downPayment}%</label>
                    <input type="range" min="5" max="50" value={downPayment} onChange={(e) => setDownPayment(parseInt(e.target.value))} />
                    <span className="re-mortgage-val">${formatNumber(Math.round(p.price * downPayment / 100))}</span>
                  </div>
                  <div className="re-mortgage-control">
                    <label>Interest Rate: {interestRate}%</label>
                    <input type="range" min="2" max="12" step="0.1" value={interestRate} onChange={(e) => setInterestRate(parseFloat(e.target.value))} />
                  </div>
                  <div className="re-mortgage-control">
                    <label>Loan Term</label>
                    <div className="re-term-btns">
                      {[15, 20, 30].map((t) => (
                        <button key={t} className={loanTerm === t ? 'active' : ''} onClick={() => setLoanTerm(t)}>{t} yr</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="re-mortgage-summary">
                  <div className="re-mortgage-row"><span>Loan Amount</span><span>${formatNumber(Math.round(p.price * (1 - downPayment / 100)))}</span></div>
                  <div className="re-mortgage-row"><span>Total Interest</span><span>${formatNumber(Math.round(totalInterest))}</span></div>
                  <div className="re-mortgage-row total"><span>Total Cost</span><span>${formatNumber(Math.round(totalPayment))}</span></div>
                </div>
              </div>
            </div>
          )}

          {/* Contact Tab */}
          {activeTab === 'contact' && (
            <div className="re-tab-content">
              <div className="re-agent-card">
                <div className="re-agent-avatar">{p.agent.avatar}</div>
                <div className="re-agent-info">
                  <h3>{p.agent.name}</h3>
                  <span className="re-agent-specialty">{p.agent.specialty}</span>
                  <div className="re-agent-stats-row">
                    <span>⭐ {p.agent.rating}</span>
                    <span>🤝 {p.agent.deals} deals</span>
                  </div>
                  <div className="re-agent-contact">
                    <span>📞 {p.agent.phone}</span>
                    <span>✉️ {p.agent.email}</span>
                  </div>
                </div>
              </div>
              {formSent ? (
                <div className="re-form-success">
                  <span>✅</span>
                  <h3>Message Sent!</h3>
                  <p>The agent will contact you shortly.</p>
                </div>
              ) : (
                <div className="re-contact-form">
                  <h3>Schedule a Viewing</h3>
                  <input type="text" placeholder="Your Name" value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} />
                  <input type="email" placeholder="Your Email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} />
                  <textarea rows={3} value={contactForm.message} onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })} />
                  <button className="re-send-btn" onClick={() => setFormSent(true)}>Send Message</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PropertyModal;
